import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { Clock, MoreVertical, Plus } from 'lucide-react'
import { notification, Dropdown, Switch } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import {
  createSchedule,
  updateSchedule,
  deleteSchedule,
} from '@/api/robot-schedule.api'
import type { TimeRange } from '../robot-position-setup/types'
import { isEndTimeAfterStartTime } from '../robot-position-setup/time-utils'
import { TimeRangePickerRow } from './TimeRangePickerRow'

interface ScheduleListPanelProps {
  robotId: string
  locationId: string
  isActive?: boolean
  schedules: TimeRange[]
  isLoading?: boolean
  isAddmode?: boolean
  onSchedulesChange?: () => void
  onLocalTimeRangesChange?: (ranges: TimeRange[]) => void
}

interface EditingSchedule {
  id?: string
  startTime: string
  endTime: string
  locationId: string
}

const createEmptyScheduleForm = (locationId: string): EditingSchedule => ({
  startTime: '',
  endTime: '',
  locationId,
})

export function ScheduleListPanel({
  robotId,
  locationId,
  isActive = true,
  schedules,
  isLoading,
  isAddmode = false,
  onSchedulesChange,
  onLocalTimeRangesChange,
}: ScheduleListPanelProps) {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingForm, setEditingForm] = useState<EditingSchedule | null>(null)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [confirmDeleteScheduleId, setConfirmDeleteScheduleId] = useState<
    string | null
  >(null)
  const [newScheduleForm, setNewScheduleForm] = useState<EditingSchedule>(
    createEmptyScheduleForm(locationId),
  )

  const resetEditingState = useCallback(() => {
    setEditingId(null)
    setEditingForm(null)
    setIsAddingNew(false)
    setConfirmDeleteScheduleId(null)
    setNewScheduleForm(createEmptyScheduleForm(locationId))
  }, [locationId])

  useEffect(() => {
    if (isActive) return
    resetEditingState()
  }, [isActive, resetEditingState])

  useEffect(() => {
    resetEditingState()
  }, [resetEditingState])

  const formatTime = (time: string) => {
    if (!time) return ''
    return dayjs(time, 'HH:mm').format('hh:mm A')
  }

  const validateSchedulePayload = (
    payload: Pick<EditingSchedule, 'startTime' | 'endTime'>,
    missingMessage: string,
  ) => {
    if (!payload.startTime || !payload.endTime) {
      notification.error({ title: missingMessage })
      return false
    }

    if (!isEndTimeAfterStartTime(payload.startTime, payload.endTime)) {
      notification.error({
        title: 'Thời gian kết thúc phải lớn hơn thời gian bắt đầu',
      })
      return false
    }

    return true
  }

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (payload: EditingSchedule) => {
      if (!payload.startTime || !payload.endTime) {
        throw new Error('Vui lòng nhập thời gian bắt đầu và kết thúc')
      }
      if (!isEndTimeAfterStartTime(payload.startTime, payload.endTime)) {
        throw new Error('Thời gian kết thúc phải lớn hơn thời gian bắt đầu')
      }

      // Nếu đang ở chế độ Thêm mới vị trí -> Chỉ lưu tạm thời local
      if (isAddmode) {
        return {
          from: payload.startTime,
          to: payload.endTime,
          status: 1,
          scheduleId: `temp-${Date.now()}`,
        }
      }

      return createSchedule(robotId, {
        startTime: payload.startTime,
        endTime: payload.endTime,
        locationId: payload.locationId,
      })
    },
    onSuccess: async (data) => {
      if (isAddmode) {
        const newRange = data as TimeRange
        const updated = [...schedules, newRange]
        onLocalTimeRangesChange?.(updated)
        notification.success({ title: 'Thêm lịch trình tạm thành công!' })
      } else {
        notification.success({ title: 'Thêm lịch trình thành công!' })
        await queryClient.invalidateQueries({
          queryKey: ['robot-location-schedules', robotId, locationId],
        })
      }
      setIsAddingNew(false)
      setNewScheduleForm(createEmptyScheduleForm(locationId))
      onSchedulesChange?.()
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (payload: EditingSchedule) => {
      if (!payload.id || !payload.startTime || !payload.endTime) {
        throw new Error('Thiếu thông tin cần thiết')
      }
      if (!isEndTimeAfterStartTime(payload.startTime, payload.endTime)) {
        throw new Error('Thời gian kết thúc phải lớn hơn thời gian bắt đầu')
      }

      if (isAddmode) {
        return payload
      }

      return updateSchedule(robotId, {
        id: payload.id,
        startTime: payload.startTime,
        endTime: payload.endTime,
        locationId: payload.locationId,
      })
    },
    onSuccess: async (_data, payload) => {
      if (isAddmode) {
        const updated = schedules.map((s) =>
          s.scheduleId === payload.id
            ? { ...s, from: payload.startTime, to: payload.endTime }
            : s,
        )
        onLocalTimeRangesChange?.(updated)
        notification.success({ title: 'Cập nhật lịch trình tạm thành công!' })
      } else {
        notification.success({ title: 'Cập nhật lịch trình thành công!' })
        await queryClient.invalidateQueries({
          queryKey: ['robot-location-schedules', robotId, locationId],
        })
      }
      setEditingId(null)
      setEditingForm(null)
      onSchedulesChange?.()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (scheduleId: string) => {
      if (isAddmode) {
        return scheduleId
      }
      return deleteSchedule(robotId, scheduleId)
    },
    onSuccess: async (_data, scheduleId) => {
      setConfirmDeleteScheduleId(null)
      if (isAddmode) {
        const updated = schedules.filter((s) => s.scheduleId !== scheduleId)
        onLocalTimeRangesChange?.(updated)
        notification.success({ title: 'Xóa lịch trình tạm thành công!' })
      } else {
        notification.success({ title: 'Xóa lịch trình thành công!' })
        await queryClient.invalidateQueries({
          queryKey: ['robot-location-schedules', robotId, locationId],
        })
      }
      onSchedulesChange?.()
    },
  })

  const toggleStatusMutation = useMutation({
    mutationFn: async ({
      schedule,
      newStatus,
    }: {
      schedule: TimeRange
      newStatus: number
    }) => {
      if (!schedule.scheduleId) throw new Error('Schedule ID không hợp lệ')
      if (isAddmode) {
        return { schedule, newStatus }
      }
      return updateSchedule(robotId, {
        id: schedule.scheduleId,
        startTime: schedule.from,
        endTime: schedule.to,
        locationId,
        status: newStatus,
      })
    },
    onSuccess: async (_data, { schedule, newStatus }) => {
      if (isAddmode) {
        const updated = schedules.map((s) =>
          s.scheduleId === schedule.scheduleId
            ? { ...s, status: newStatus }
            : s,
        )
        onLocalTimeRangesChange?.(updated)
        notification.success({
          title:
            newStatus === 1
              ? 'Bật trạng thái lịch trình thành công!'
              : 'Tắt trạng thái lịch trình thành công!',
        })
      } else {
        notification.success({
          title:
            newStatus === 1
              ? 'Bật trạng thái lịch trình thành công!'
              : 'Tắt trạng thái lịch trình thành công!',
        })
        await queryClient.invalidateQueries({
          queryKey: ['robot-location-schedules', robotId, locationId],
        })
      }
      onSchedulesChange?.()
    },
  })

  const handleStartEditSchedule = (schedule: TimeRange) => {
    if (!schedule.scheduleId) {
      notification.error({ title: 'Không tìm thấy mã lịch trình' })
      return
    }
    setEditingId(schedule.scheduleId)
    setEditingForm({
      id: schedule.scheduleId,
      startTime: schedule.from,
      endTime: schedule.to,
      locationId,
    })
  }

  const handleSaveEdit = () => {
    if (!editingForm) return
    if (
      !validateSchedulePayload(
        editingForm,
        'Vui lòng nhập thời gian bắt đầu và kết thúc',
      )
    ) {
      return
    }

    updateMutation.mutate(editingForm)
  }

  const handleSaveNew = () => {
    if (
      !validateSchedulePayload(
        newScheduleForm,
        'Vui lòng nhập thời gian bắt đầu và kết thúc',
      )
    ) {
      return
    }

    createMutation.mutate(newScheduleForm)
  }

  const handleDeleteClick = (scheduleId?: string) => {
    if (!scheduleId) {
      notification.error({ title: 'ID lịch trình không hợp lệ' })
      return
    }
    setConfirmDeleteScheduleId(scheduleId)
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-[#f6f7fb] p-4 text-center text-gray-400">
        Đang tải lịch trình...
      </div>
    )
  }

  return (
    <>
      <ConfirmModal
        isOpen={!!confirmDeleteScheduleId}
        title="Xóa lịch trình?"
        description="Hành động này sẽ xóa lịch trình khỏi vị trí hiện tại."
        cancelText="Hủy"
        confirmText="Xóa"
        variant="danger"
        isLoading={deleteMutation.isPending}
        onClose={() => setConfirmDeleteScheduleId(null)}
        onConfirm={() => {
          if (!confirmDeleteScheduleId) return
          deleteMutation.mutate(confirmDeleteScheduleId)
        }}
      />

      <div className="flex max-h-[500px] flex-col gap-3 overflow-y-auto pr-1">
        <div className="flex flex-col gap-2">
          {schedules.map((schedule, idx) => {
            const rowId = schedule.scheduleId ?? `row-${idx}`
            const isEditing = editingId === rowId && !!editingForm

            return (
              <div
                key={rowId}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2 text-[14px] font-medium text-[#6b7280]">
                    <Clock size={16} className="shrink-0 text-[#6b7280]" />
                    <span className="truncate">
                      {formatTime(schedule.from)} - {formatTime(schedule.to)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={(schedule.status ?? 1) === 1}
                      onChange={(checked) =>
                        toggleStatusMutation.mutate({
                          schedule,
                          newStatus: checked ? 1 : 0,
                        })
                      }
                      loading={toggleStatusMutation.isPending}
                      size="small"
                    />

                    <Dropdown
                      menu={{
                        className: 'min-w-[120px] rounded-xl p-1',
                        items: [
                          {
                            key: 'update',
                            label: (
                              <span className="font-medium text-[#CE7A58]">
                                Cập nhật
                              </span>
                            ),
                            className:
                              'rounded-lg !px-3 !py-2 hover:!bg-[#CE7A581A] active:!bg-[#fff3eb]',
                            onClick: () => handleStartEditSchedule(schedule),
                          },
                          {
                            key: 'delete',
                            label: (
                              <span className="font-medium text-[#CE7A58]">
                                Xóa
                              </span>
                            ),
                            className:
                              'rounded-lg !px-3 !py-2 hover:!bg-[#CE7A581A] active:!bg-[#fee2e2]',
                            onClick: () =>
                              handleDeleteClick(schedule.scheduleId),
                          },
                        ],
                      }}
                      trigger={['click']}
                    >
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        <MoreVertical size={14} />
                      </Button>
                    </Dropdown>
                  </div>
                </div>

                {isEditing && (
                  <div className="mt-2 border-t border-gray-200 pt-3">
                    <div className="text-[18px] font-semibold text-[#4b5563]">
                      Thiết lập thời gian
                    </div>
                    <div className="mt-2">
                      <TimeRangePickerRow
                        from={
                          editingForm.startTime
                            ? dayjs(editingForm.startTime, 'HH:mm')
                            : undefined
                        }
                        to={
                          editingForm.endTime
                            ? dayjs(editingForm.endTime, 'HH:mm')
                            : undefined
                        }
                        onFromChange={(time) =>
                          setEditingForm({
                            ...editingForm,
                            startTime: time ? time.format('HH:mm') : '',
                          })
                        }
                        onToChange={(time) =>
                          setEditingForm({
                            ...editingForm,
                            endTime: time ? time.format('HH:mm') : '',
                          })
                        }
                        fromPlaceholder="hh:mm"
                        toPlaceholder="hh:mm"
                      />
                    </div>

                    <div className="mt-3 flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingId(null)
                          setEditingForm(null)
                        }}
                        className="min-w-[110px] h-10"
                      >
                        Hủy
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        loading={updateMutation.isPending}
                        onClick={handleSaveEdit}
                        className="min-w-[120px] h-10"
                      >
                        Lưu
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className="border-t border-gray-200 pt-3">
          {isAddingNew ? (
            <div className="rounded-xl border border-gray-200 bg-white p-3">
              <div className="text-[18px] font-semibold text-[#4b5563]">
                Thêm mới lịch trình
              </div>
              <div className="flex flex-col gap-2">
                <div className="mt-2">
                  <TimeRangePickerRow
                    from={
                      newScheduleForm.startTime
                        ? dayjs(newScheduleForm.startTime, 'HH:mm')
                        : undefined
                    }
                    to={
                      newScheduleForm.endTime
                        ? dayjs(newScheduleForm.endTime, 'HH:mm')
                        : undefined
                    }
                    onFromChange={(time) =>
                      setNewScheduleForm({
                        ...newScheduleForm,
                        startTime: time ? time.format('HH:mm') : '',
                      })
                    }
                    onToChange={(time) =>
                      setNewScheduleForm({
                        ...newScheduleForm,
                        endTime: time ? time.format('HH:mm') : '',
                      })
                    }
                    fromPlaceholder="hh:mm"
                    toPlaceholder="hh:mm"
                  />
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsAddingNew(false)
                      setNewScheduleForm(createEmptyScheduleForm(locationId))
                    }}
                    className="min-w-[110px] h-10"
                  >
                    Hủy
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    loading={createMutation.isPending}
                    onClick={handleSaveNew}
                    className="min-w-[120px] h-10"
                  >
                    Lưu
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAddingNew(true)}
              className="w-full h-10 border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10"
            >
              <Plus size={16} className="mr-1" />
              Thêm mới
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
